<template>
  <page-layout>
    <template #header-title>
      <back-link href="/">
        {{ $trlMessage('header.applicants_and_vacancies') }}
      </back-link>
    </template>
    <template #content>
      <div :class="$style.wrapper">
        <h1 :class="$style.caption">{{ $trlMessage('page.settings.title') }}</h1>

        <tab-group :tab="currentCommonPage">
          <template #tabs>
            <base-tab
              v-for="page in commonPages"
              :key="page.name"
              :name="page.name"
              :href="page.to"
              @selected="changeUrl(page.to)"
            >
              {{ page.text }}
            </base-tab>
          </template>
          <template #content>
            <div ref="organizationName" :class="$style.organizationName">
              <div>
                {{ organization.name }}
              </div>
              <span :class="$style.organizationId" @click="handleCopyId">
                ID {{ organization.id }}
              </span>
            </div>

            <div class="row" :class="$style.mainLayout">
              <div class="col-xs-3">
                <base-nav v-if="pages.length" :items="pages" />
              </div>
              <div :class="contentClasses">
                <router-view>
                  <template #default="{ Component }">
                    <transition
                      name="fadeIn"
                      mode="out-in"
                      :enter-active-class="$style.activeAnimation"
                      :leave-active-class="$style.activeAnimation"
                      :enter-class="$style.fadeAnimation"
                      :leave-to-class="$style.fadeAnimation"
                    >
                      <component :is="Component" @should-scroll="scrollIntoView" />
                    </transition>
                  </template>
                </router-view>
              </div>
            </div>
          </template>
        </tab-group>
      </div>
      <router-view name="extra">
        <template #default="{ Component }">
          <transition
            name="fadeIn"
            mode="out-in"
            :enter-active-class="$style.activeAnimation"
            :leave-active-class="$style.activeAnimation"
            :enter-class="$style.fadeAnimation"
            :leave-to-class="$style.fadeAnimation"
          >
            <component :is="Component" />
          </transition>
        </template>
      </router-view>
    </template>
  </page-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import { getOverflowAncestors } from '@floating-ui/dom';

import { notification } from '@/services/notification/notification';
import { AnimateHelper } from '@/util/animate-helper';
import PageLayout from '@/components/page-layout/page-layout.vue';
import BaseTab from '@/components/tabs/base-tab';
import TabGroup from '@/components/tabs/tab-group';
import BackLink from '@/components/page-layout/back-link.vue';
import BaseNav from './base-nav';

export default {
  name: 'OrganizationSettingsApp',
  components: { BackLink, PageLayout, BaseNav, BaseTab, TabGroup },
  props: {
    currentCommonPage: {
      type: String,
      required: true
    },
    commonPages: {
      type: Array,
      required: true
    },
    pages: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      scrollInProcess: false
    };
  },
  computed: {
    ...mapGetters('config', ['organization']),
    contentClasses() {
      return [this.$style.content, this.$route.meta?.fullPage ? 'col-xs-9' : 'col-xs-7'];
    }
  },
  watch: {
    $route(route, prevRoute) {
      if (route.path === prevRoute.path) {
        return;
      }
      this.scrollIntoView();
    }
  },
  methods: {
    scrollIntoView(offset) {
      const { offsetTop, offsetHeight } = this.$refs.organizationName;
      const scrollPosition = offset || offsetTop + offsetHeight;
      const overflowAncestor = getOverflowAncestors(this.$refs.organizationName)[0];
      if (overflowAncestor.scrollTop > scrollPosition) {
        AnimateHelper.scrollTo(
          overflowAncestor,
          { top: scrollPosition },
          {
            easing: AnimateHelper.easeOutCubic,
            end: () => (this.scrollInProcess = false)
          }
        );
      }
    },
    changeUrl(href) {
      window.location.href = href;
    },
    handleCopyId() {
      const promise = navigator.clipboard.writeText(this.organization.id);
      promise.then(() => {
        notification.notify({
          content: this.$trlMessage('settings.id.copied', {
            id: this.organization.id
          }),
          isError: false
        });
      });
    }
  }
};
</script>

<style module lang="less">
@import '~@less/common/mixins/font';

.wrapper {
  padding: 0 15px;
  max-width: 75%;
  position: relative;

  min-height: unset;

  composes: col-xs-offset-2 col-xs-9 from global;
}
.caption {
  font-size: 22px;
  line-height: 30px;
  .font_bold();
  padding: 0;
  margin: 40px 0 30px;
}

.activeAnimation {
  transition: opacity 100ms;
}
.fadeAnimation {
  opacity: 0;
}

.organizationName {
  padding-top: 20px;
  padding-bottom: 14px;
  border-bottom: 3px solid #eaeaea;
  font-size: 25px;
  line-height: 28px;
  margin: 0;
  .font_regular();
}

.organizationId {
  font-size: 14px;
  line-height: 18px;
  color: #999;
  cursor: pointer;
  margin-top: 10px;

  &:hover {
    color: #db525e;
  }
}

.mainLayout {
  padding-bottom: 100px;
}
.content {
  padding-top: 30px;
  position: relative;
}
</style>

<i18n lang="json">{}</i18n>
